import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Label,
  Modal,
  Form,
  Search,
  Icon,
  Select,
  Popup,
} from "semantic-ui-react";
import { useToasts } from "react-toast-notifications";
import RestoreModal from "./mdl-undo/mdl-undo";
import MDL_PROGRESS from "../../../components/cmp-progress/cmp-progress";
import MDL_ADD_CRITICAL_UNIT from "./mdl-add-critical-unit/mdl-add-critical-unit";
import MDL_UPDATE_CRITICAL_UNIT from "./mdl-update-critical-unit/mdl-update-critical-unit";
import MDL_CRITICAL_SUMMARY from "./mdl-critical-summary/mdl-critical-summary";
import MDL_SEND_FOLLOWUP from "./mdl-send-followup/mdl-send-followup";
import {
  CriticalListData,
  CriticalUnitYears,
  CriticalUnitsStatusCounts,
  getListofScheduler,
  getIssueTagsLookup,
  schedulerLocationLookup,
  sendCriticalFollowupEmail,
  sendCriticalFollowupEmailForUnit,
  UpdateCriticalUnitData,
} from "../../../constants";
import AppPagination from "../../../components/cmp-pagination/Pagination";
import { useHistory } from "react-router-dom";
import "./CriticalList.css";
import moment from "moment";
import MDL_UNIT_HISTORY from "../../scheduler/BuildingHeatmap/Components/mdl_unit_history/mdl_unit_history";
import MDL_VIEW_DETAILS from "./mdl-view-details/mdl-view-details";
import { AppContext } from "../../../Context/AppContext";
import { is } from "date-fns/locale";
import YearPicker from "./YearPicker/YearPicker";

// Traffic-light indicator for the LSD (Last Scheduled Date) column:
// green = a service order is already booked (order_status = 'scheduled')
// for this building/unit/pest, per has_scheduled_order from the backend
// (get-all-critical-units.js — matched via service_order + order_unit_detail,
// not just guessed from the date). Only when nothing is actually scheduled
// do we fall back to flagging staleness from the LSD: yellow at 2+ weeks,
// red at 4+ weeks. Under 2 weeks with nothing scheduled isn't yet a concern,
// so no badge is shown (a neutral state is planned for later).
function getLsdSignal(lastServiceDate, hasScheduledOrder) {
  if (hasScheduledOrder) {
    return { color: "#43a047", label: "Scheduled" };
  }
  if (!lastServiceDate) return null;
  const lsd = moment(lastServiceDate);
  if (!lsd.isValid()) return null;
  const daysSince = moment().startOf("day").diff(lsd.startOf("day"), "days");
  if (daysSince >= 28) {
    return {
      color: "#e53935",
      label: "No treatment for 4 weeks from the LSD",
    };
  }
  if (daysSince >= 14 && daysSince < 28) {
    return {
      color: "#fbc02d",
      label: "No treatment for 2 weeks from the LSD",
    };
  }
  return null;
}

// Persists the list's page/sort across a trip out to building-impersonate
// (and back), so returning shows the same view instead of resetting to
// defaults. sessionStorage survives the component unmount that happens on
// route change, but clears when the tab closes.
// Search, critical-type and scheduler are intentionally excluded: the list
// should always come back to a blank search on "Ongoing" with the backend's
// default scheduler (the one matching the user's associated email, or "All"
// when there's no match), never restore a stale filter from a previous visit.
const CRITICAL_LIST_STATE_KEY = "criticalListState";

function readSavedCriticalListState() {
  try {
    const raw = sessionStorage.getItem(CRITICAL_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function CriticalList() {
  /* VARIABLE DECLARATIONS
   ---------------------------------------------------------------------------------*/
  const savedListState = readSavedCriticalListState();
  const {
    userInfo,
    appVersion,
    isAppVersionOutdated,
    criticalUnitDeletion,
    defaultPane,
  } = useContext(AppContext);
  const [user] = userInfo;
  const [, set_default_pane] = defaultPane;
  const [var_app_version] = appVersion;
  const [, set_is_app_version_outdated] = isAppVersionOutdated;
  const [isCriticalUnitDeleted, setIsCriticalUnitDeleted] =
    criticalUnitDeletion;
  const [selected_unit, set_selected_unit] = useState(null);
  const [CriticalData, setCriticalData] = useState([]);
  const [SingleCriticalData, setSingleCriticalData] = useState([]);
  const history = useHistory();
  const [page, setPage] = useState(savedListState?.page || 1);
  const [limit, setLimit] = useState(savedListState?.limit || 10);
  const [, setTotal] = useState(0);
  const [searchResult, setSearchResult] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [var_mdl_critical_detail_open, set_mdl_critical_detail_open] =
    useState(false);
  const [
    var_mdl_update_critical_detail_open,
    set_mdl_update_critical_detail_open,
  ] = useState(false);
  const [var_mdl_summary_open, set_mdl_summary_open] = useState(false);
  const [current_sort, set_current_sorted] = useState(
    savedListState?.current_sort || {
      sort_by: "created_at",
      sort_by_value: "DESC",
    },
  );
  const { addToast } = useToasts();
  const [var_scheduler_list, set_var_scheduler_list] = useState({
    value: "",
    text: "",
    email: "",
  });
  const [critical_types] = useState([
    {
      text: "Ongoing",
      value: "ongoing",
    },
    {
      text: "S.W.A.T",
      value: "supervisor",
    },
    {
      text: "YTD Resolved",
      value: "resolved",
    },
    {
      text: "Deleted",
      value: "deleted",
    },
  ]);
  const [var_critcal_type, set_critcal_type] = useState("ongoing");
  const [var_mdl_progress, set_mdl_progress] = useState(false);
  const [schedulers, setSchedulers] = useState([]);
  const [var_mdl_unit_history, set_mdl_unit_history] = useState(false);
  const [var_refresh, set_refresh] = useState(false);
  const [var_downlaod, set_downlaod] = useState(false);
  const [downloadYear, setDownloadYear] = useState(null);
  const [availableDownloadYears, setAvailableDownloadYears] = useState([]);
  const [var_mdl_view_detail_open, set_mdl_view_detail_open] = useState(false);
  const [var_detail_data, set_detail_data] = useState("");
  const [var_detail_title, set_detail_title] = useState("");
  const [building_id, set_building_id] = useState("");
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isFollowupModalOpen, setIsFollowupModalOpen] = useState(false);
  const [followupError, setFollowupError] = useState("");
  const [recordToRestore, setRecordToRestore] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [issueTagColorMap, setIssueTagColorMap] = useState({});
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    ongoing: 0,
    supervisor: 0,
    ytdResolved: 0,
  });
  const [statusCountsLoading, setStatusCountsLoading] = useState(false);
  const [sendingFollowup, setSendingFollowup] = useState(false);
  // Per-row "Send Follow-up" in-flight tracking, keyed by critical unit uuid.
  const [unitFollowupDisabled, setUnitFollowupDisabled] = useState({});
  const prevPageRef = useRef(page);
  const prevSearchRef = useRef("");
  const lastLoadedPageRef = useRef(0);
  /* EVENT LISTENERS
  ---------------------------------------------------------------------------------*/
  // useEffect(() => {
  //   get_all_critical_list(1,limit);
  // }, [var_mdl_critical_detail_open,var_scheduler_list,var_critcal_type,var_mdl_update_critical_detail_open,var_refresh]);

  // useEffect(() => {
  //   get_all_critical_list(page,limit);
  // }, [page, limit,var_downlaod]);

  // useEffect(() => {
  //   setPage(1);
  //   get_all_critical_list(page,limit);
  // }, [sortBy,current_sort]);

  const isFirstSortRef = useRef(true);
  useEffect(() => {
    // Skip the reset-to-page-1 on initial mount so a restored page (from
    // returning via building-impersonate) isn't immediately wiped out.
    if (isFirstSortRef.current) {
      isFirstSortRef.current = false;
      return;
    }
    setPage(1);
  }, [current_sort]);

  useEffect(() => {
    fetchIssueTagsLookup();
  }, []);

  // Keep the "where we left off" snapshot up to date so a round trip through
  // building-impersonate (via the building-address link) restores this same
  // view instead of resetting to defaults.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        CRITICAL_LIST_STATE_KEY,
        JSON.stringify({
          page,
          limit,
          current_sort,
        }),
      );
    } catch {
      // sessionStorage unavailable (private mode etc.) — safe to no-op.
    }
  }, [page, limit, current_sort]);

  useEffect(() => {
    if (!var_scheduler_list.value) {
      return;
    }

    const isLoadMore = page > prevPageRef.current;
    prevPageRef.current = page;
    const searchChanged = prevSearchRef.current !== searchQuery;
    prevSearchRef.current = searchQuery;

    const delayDebounceFn = setTimeout(
      () => {
        get_all_critical_list(page, limit, searchQuery, { append: isLoadMore });
      },
      searchChanged && searchQuery ? 1200 : 0,
    );

    return () => clearTimeout(delayDebounceFn);
  }, [
    current_sort,
    page,
    limit,
    var_downlaod,
    var_scheduler_list,
    var_critcal_type,
    searchQuery,
    listRefreshKey,
  ]);

  // After delete from update modal — refresh loaded pages without jumping to page 1
  useEffect(() => {
    if (isCriticalUnitDeleted) {
      setIsCriticalUnitDeleted(false);
      setListRefreshKey((key) => key + 1);
    }
  }, [isCriticalUnitDeleted]);

  // Refresh list after add/update without resetting the user back to page 1
  function refreshCriticalList() {
    setListRefreshKey((key) => key + 1);
  }

  useEffect(() => {
    if (var_scheduler_list.value && var_refresh) {
      set_refresh(false);
      getSchedulerData();
    }
  }, [var_refresh]);

  useEffect(() => {
    getSchedulerData();
  }, []);

  // Load years that actually have resolved critical units for the selected scheduler
  useEffect(() => {
    if (var_critcal_type !== "resolved" || !var_scheduler_list.value) {
      setAvailableDownloadYears([]);
      return;
    }
    fetchAvailableDownloadYears();
  }, [var_critcal_type, var_scheduler_list.value]);

  // useEffect(() => {
  //   if(schedulers){
  //     set_var_scheduler_list({"value":schedulers[0].value,"text":schedulers[0].text,"email":schedulers[0].email})
  //   }
  // }, [schedulers]);

  // Keep the stat cards in sync with the selected scheduler and whenever the
  // list is refreshed (add/update/delete/restore).
  useEffect(() => {
    if (!var_scheduler_list.value) {
      return;
    }
    fetchStatusCounts();
  }, [var_scheduler_list.value, listRefreshKey]);

  /* ASYNC FUNCTIONS
  ---------------------------------------------------------------------------------*/
  async function fetchStatusCounts() {
    setStatusCountsLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const schedulerParam =
        var_scheduler_list.value === "all" ? "all" : var_scheduler_list.value;
      const response = await fetch(
        `${CriticalUnitsStatusCounts}?schedulerID=${encodeURIComponent(
          schedulerParam,
        )}`,
        requestOptions,
      );
      const res = await response.json();
      if (res?.version && res?.version !== var_app_version) {
        set_is_app_version_outdated(true);
      }
      if (res.code === 200 && res.data) {
        setStatusCounts({
          total: Number(res.data.total) || 0,
          ongoing: Number(res.data.ongoing) || 0,
          supervisor: Number(res.data.supervisor) || 0,
          ytdResolved: Number(res.data.ytd_resolved) || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching critical unit status counts:", error);
    } finally {
      setStatusCountsLoading(false);
    }
  }

  async function fetchIssueTagsLookup() {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const response = await fetch(getIssueTagsLookup, requestOptions);
      const res = await response.json();
      if (res?.version && res?.version !== var_app_version) {
        set_is_app_version_outdated(true);
      }
      if (res.code === 200 && Array.isArray(res.data)) {
        const colorMap = {};
        res.data.forEach((tag) => {
          colorMap[tag.value] = tag.color;
        });
        setIssueTagColorMap(colorMap);
      }
    } catch (error) {
      console.error("Error fetching issue tags lookup:", error);
    }
  }

  async function fetchAvailableDownloadYears() {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const schedulerParam =
        var_scheduler_list.value === "all" ? "all" : var_scheduler_list.value;
      const response = await fetch(
        `${CriticalUnitYears}?status=resolved&schedulerID=${encodeURIComponent(
          schedulerParam,
        )}`,
        requestOptions,
      );
      const res = await response.json();
      if (res?.version && res?.version !== var_app_version) {
        set_is_app_version_outdated(true);
      }
      const years =
        res.code === 200 && Array.isArray(res.data)
          ? res.data.map((y) => Number(y)).filter((y) => !Number.isNaN(y))
          : [];
      setAvailableDownloadYears(years);
      if (years.length > 0) {
        setDownloadYear((prev) =>
          years.includes(Number(prev)) ? Number(prev) : years[0],
        );
      } else {
        setDownloadYear(null);
      }
    } catch (error) {
      console.error("Error fetching critical unit years:", error);
      setAvailableDownloadYears([]);
      setDownloadYear(null);
    }
  }
  async function fetchCriticalListPage(page_no, limit_range, searchValue) {
    const query_params = searchValue
      ? `?q=${encodeURIComponent(searchValue)}&page=${page_no}&limit=${limit_range}`
      : `?page=${page_no}&limit=${limit_range}`;
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Token " + user.token,
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    const criticalApi =
      CriticalListData +
      query_params +
      `&order_by=${current_sort?.sort_by || ""}&order_by_value=${
        current_sort?.sort_by_value || ""
      }&schedulerID=${
        var_scheduler_list.value === "all" ? "all" : var_scheduler_list.value
      }&status=${var_critcal_type}&isExport=false`;

    const response = await fetch(criticalApi, requestOptions);
    const res = await response.json();
    if (res?.version && res?.version !== var_app_version) {
      set_is_app_version_outdated(true);
    }
    return res;
  }

  async function get_all_critical_list(page_no, limit_range, q, options = {}) {
    const searchValue = q ?? searchQuery;
    const requestedPage = page_no ? page_no : page;
    const pageLimit = limit_range ? limit_range : limit;
    const shouldAppend = options.append === true && requestedPage > 1;

    set_mdl_progress(true);
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Token " + user.token,
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };

    try {
      if (var_downlaod) {
        const query_params = searchValue
          ? `?q=${encodeURIComponent(searchValue)}&page=${requestedPage}&limit=${pageLimit}`
          : `?page=${requestedPage}&limit=${pageLimit}`;
        const exportYearQuery =
          var_critcal_type === "resolved" && downloadYear
            ? `&exportYear=${encodeURIComponent(downloadYear)}`
            : "";
        const criticalApi =
          CriticalListData +
          query_params +
          `&order_by=${current_sort?.sort_by || ""}&order_by_value=${
            current_sort?.sort_by_value || ""
          }&schedulerID=${
            var_scheduler_list.value === "all" ? "all" : var_scheduler_list.value
          }&status=${var_critcal_type}&isExport=${var_downlaod}${exportYearQuery}`;

        const response = await fetch(criticalApi, requestOptions);
        if (!response.ok) {
          throw new Error("Failed to fetch the file");
        }

        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const res = await response.json();
          addToast(res.failed || "No critical units found for the selected year", {
            appearance: "error",
            autoDismiss: true,
          });
          set_downlaod(false);
          return;
        }

        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          addToast("No critical units found for the selected year", {
            appearance: "error",
            autoDismiss: true,
          });
          set_downlaod(false);
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `critical_list_${downloadYear || "export"}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        set_downlaod(false);
        return;
      }

      // Non-append refresh on page > 1: reload pages 1..N so the user keeps
      // their loaded window (no jump to page 1, no duplicate append).
      if (!shouldAppend && requestedPage > 1) {
        let allRows = [];
        let totalCount = 0;
        for (let p = 1; p <= requestedPage; p++) {
          const res = await fetchCriticalListPage(p, pageLimit, searchValue);
          if (res.code === 200) {
            totalCount = res.total_count;
            allRows = [...allRows, ...(res.data || [])];
          } else if (p === 1) {
            allRows = [];
            break;
          }
        }
        setTotal(totalCount);
        lastLoadedPageRef.current = requestedPage;
        setCriticalData(allRows);
        if (var_refresh) {
          addToast("Successfully updated critical list", {
            appearance: "success",
            autoDismiss: true,
          });
          set_refresh(false);
        }
        return;
      }

      const res = await fetchCriticalListPage(
        requestedPage,
        pageLimit,
        searchValue,
      );
      let CriticalUnitFetch = [];
      setTotal(res.total_count);

      if (res.code === 200) {
        CriticalUnitFetch = res.data || [];
        if (res.page_number > 1 && CriticalUnitFetch.length === 0) {
          setPage(res.page_number - 1);
        }
        if (var_refresh) {
          addToast("Successfully updated critical list", {
            appearance: "success",
            autoDismiss: true,
          });
          set_refresh(false);
        }
      }

      if (shouldAppend) {
        setCriticalData((prev) => [...prev, ...CriticalUnitFetch]);
      } else {
        setCriticalData(CriticalUnitFetch);
      }
      lastLoadedPageRef.current = requestedPage;
    } catch (error) {
      console.error("Error building get:", error);
    } finally {
      set_mdl_progress(false);
    }
  }

  function handleEditCriticalUnit(data) {
    setSingleCriticalData(data);
    set_mdl_update_critical_detail_open(true);
  }

  async function UpdateCriticalData(unitDetails) {
    set_mdl_progress(true);
    unitDetails.date_reported = convertDateFormat(
      moment(unitDetails.date_reported).toDate(),
    ).replace("Z", "");
    unitDetails.last_service_date = convertDateFormat(
      moment(unitDetails.last_service_date).toDate(),
    ).replace("Z", "");
    const requestWarranty = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Token " + user.token,
        entityid: user.uuid,
        appversion: var_app_version,
      },
      body: JSON.stringify(unitDetails),
    };
    await fetch(UpdateCriticalUnitData + unitDetails?.uuid, requestWarranty)
      .then((response) => response.json())
      .then(async (res) => {
        if (res.code == 200) {
          addToast(res.success, {
            appearance: "success",
            autoDismiss: true,
          });
          if (page === 1) {
            await get_all_critical_list(page, limit);
            fetchStatusCounts();
          } else {
            setPage(1);
          }
        } else {
          if (res?.version && res?.version !== var_app_version) {
            set_is_app_version_outdated(true);
          }
          addToast(res.failed, {
            appearance: "error",
            autoDismiss: true,
          });
          set_mdl_progress(false);
        }
      })
      .catch((error) => {
        set_mdl_progress(false);
        console.log("Errors:", error);
      });
  }

  /* EVENT FUNCTIONS
   ---------------------------------------------------------------------------------*/
  async function doSearch(event) {
    const value = event.target.value.toString().toLowerCase();
    setSearchQuery(value);
    setPage(1);
  }

  function swap(a, b) {
    let items = CriticalData;
    items[a] = items.splice(b, 1, items[a])[0];
    items?.forEach((items, index) => {
      items.priority = index;
    });
    setCriticalData([...items]);
  }

  async function getSchedulerData() {
    set_mdl_progress(true);
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    try {
      // const response = await fetch(getListofScheduler, requestHeaders);
      const response = await fetch(
        schedulerLocationLookup + `/${user.uuid}`,
        requestHeaders,
      );
      const res = await response.json();
      if (res.code !== 200) {
        if (res?.version && res?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
        set_mdl_progress(false);
      }
      // Format the schedulers and add the "All" option
      // const formattedSchedulers = [
      //   { value: "all", text: "All" }, // Add the "All" option
      //   ...res.data.map((scheduler) => ({
      //     value: scheduler.value,
      //     text: `${scheduler.text} (${scheduler.email})`,
      //   })),
      // ];
      setSchedulers(
        res.data.map((scheduler) => ({
          value: scheduler.value,
          text: scheduler.text,
          email: scheduler.email,
          isSelected: scheduler.isSelected || false,
        })),
      );
      // Always use the server's default pick (the scheduler matching the
      // user's associated email, or "All" when there's no match) — the
      // scheduler filter is not restored from a previous visit.
      const selectedScheduler =
        res.data.find((option) => option.isSelected) || res.data[0];
      set_var_scheduler_list({
        value: selectedScheduler?.value || "",
        text: selectedScheduler?.text || "",
        email: selectedScheduler?.email || "",
      });
      set_mdl_progress(false);
    } catch (error) {
      console.error("Error:", error);
      set_mdl_progress(false);
    }
  }

  const convertDateFormat = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString();
  };

  const onChangeScheduler = (e, { value }) => {
    const selectedScheduler = schedulers.find(
      (option) => option.value === value,
    );
    set_var_scheduler_list({
      value,
      text: selectedScheduler?.text || "",
      email: selectedScheduler?.email || "",
    });
    setPage(1);
  };

  const onChangeCriticalType = (e, { value }) => {
    setPage(1);
    set_critcal_type(value);
  };

  const onChangeDownloadYear = (year) => {
    setDownloadYear(year);
  };

  function handleModalUnitHistory(buildingID, unit) {
    set_selected_unit(unit);
    set_building_id(buildingID);
    if (unit) {
      openModalUnitHistory();
    } else {
      closeModalUnitHistory();
    }
  }

  function openModalUnitHistory() {
    set_mdl_unit_history(true);
  }

  function closeModalUnitHistory() {
    set_mdl_unit_history(false);
  }

  function truncateText(text, limit) {
    if (text && text?.length > limit) {
      return (
        <>
          {text.substring(0, limit)}...
          <span
            style={{ color: "blue", cursor: "pointer" }}
            onClick={() => HandleMdlDetails(text, "Last Tech Comment")}
          >
            Read more
          </span>
        </>
      );
    }
    return text;
  }

  function HandleMdlDetails(data, title) {
    set_detail_data(data);
    set_detail_title(title);
    if (data?.length > 0) {
      set_mdl_view_detail_open(true);
    }
  }

  function getSchedulerOptionLabel(option) {
    return option.text;
  }

  function formatFollowupApiError(res) {
    const mainMessage =
      res?.failed ||
      res?.message ||
      res?.error ||
      "Failed to send follow-up emails.";

    const failedResults = (res?.data?.results || []).filter(
      (item) => item && item.success === false,
    );

    if (!failedResults.length) {
      return mainMessage;
    }

    const detailLines = failedResults
      .slice(0, 5)
      .map((item) => {
        const label = item.building_address || item.buildingID || "Building";
        return `${label}: ${item.failed || "Failed to send email."}`;
      })
      .join("\n");

    const remaining = failedResults.length - 5;
    const suffix =
      remaining > 0 ? `\n...and ${remaining} more location(s) failed.` : "";

    return `${mainMessage}\n${detailLines}${suffix}`;
  }

  function openFollowupModal() {
    if (
      var_critcal_type !== "ongoing" &&
      var_critcal_type !== "supervisor"
    ) {
      addToast(
        "Follow-up emails can only be sent for Ongoing or Supervisor critical units.",
        {
          appearance: "warning",
          autoDismiss: true,
        },
      );
      return;
    }

    const availableSchedulers = (schedulers || []).filter(
      (scheduler) => scheduler?.value && scheduler.value !== "all",
    );
    if (!availableSchedulers.length) {
      addToast("No schedulers available to send follow-up emails.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    setFollowupError("");
    setIsFollowupModalOpen(true);
  }

  function getDefaultFollowupSchedulerIds() {
    if (!var_scheduler_list?.value) {
      return [];
    }
    if (var_scheduler_list.value === "all") {
      return (schedulers || [])
        .filter((scheduler) => scheduler?.value && scheduler.value !== "all")
        .map((scheduler) => scheduler.value);
    }
    return [var_scheduler_list.value];
  }

  async function handleConfirmSendFollowupEmail(selectedSchedulerIds = []) {
    const schedulerIDs = [
      ...new Set(
        (selectedSchedulerIds || [])
          .map((id) => String(id || "").trim())
          .filter((id) => id && id !== "all"),
      ),
    ];

    if (!schedulerIDs.length) {
      setFollowupError("Please select at least one scheduler.");
      return;
    }

    setFollowupError("");
    setSendingFollowup(true);
    set_mdl_progress(true);

    try {
      const response = await fetch(sendCriticalFollowupEmail, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
        body: JSON.stringify({
          schedulerIDs,
        }),
      });
      const res = await response.json();

      if (res?.version && res?.version !== var_app_version) {
        set_is_app_version_outdated(true);
      }

      if (res.code === 200) {
        setIsFollowupModalOpen(false);
        setFollowupError("");
        addToast(res.success || "Follow-up emails sent successfully.", {
          appearance: "success",
          autoDismiss: true,
        });
      } else {
        setFollowupError(formatFollowupApiError(res));
      }
    } catch (error) {
      console.error("Error sending critical follow-up emails:", error);
      setFollowupError("Failed to send follow-up emails. Please try again.");
    } finally {
      set_mdl_progress(false);
      setSendingFollowup(false);
    }
  }

  async function handleSendUnitFollowupEmail(data) {
    const criticalUnitID = data?.uuid;
    if (!criticalUnitID || unitFollowupDisabled[criticalUnitID]) {
      return;
    }

    // Disable immediately as an in-flight guard so a double-click can't fire
    // two requests for the same item; re-enabled as soon as the API responds.
    setUnitFollowupDisabled((prev) => ({ ...prev, [criticalUnitID]: true }));

    try {
      const response = await fetch(sendCriticalFollowupEmailForUnit, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
        body: JSON.stringify({ criticalUnitID }),
      });
      const res = await response.json();

      if (res?.version && res?.version !== var_app_version) {
        set_is_app_version_outdated(true);
      }

      if (res.code === 200) {
        addToast(res.success || "Follow-up email sent successfully.", {
          appearance: "success",
          autoDismiss: true,
        });
        refreshCriticalList();
      } else {
        addToast(res.failed || "Failed to send follow-up email.", {
          appearance: res.code === 204 ? "warning" : "error",
          autoDismiss: true,
        });
      }
    } catch (error) {
      console.error("Error sending unit follow-up email:", error);
      addToast("Failed to send follow-up email. Please try again.", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setUnitFollowupDisabled((prev) => {
        const next = { ...prev };
        delete next[criticalUnitID];
        return next;
      });
    }
  }

  /* RENDER APP
	---------------------------------------------------------------------------------*/
  return (
    <>
      <div className="app-content">
        <div className="app-backto-prev-page">
          <div
            className="app-goback-button"
            onClick={() => {
              // history.goBack();
              set_default_pane(0);
              history.push(`/scheduler-home`);
            }}
          >
            <Icon name="arrow left" /> Go Back
          </div>
        </div>
        <div className="app-building-container">
          <div className="app-content-action">
            <div className="app-content-action-right">
              {/* {(var_critcal_type === "ongoing" ||
                var_critcal_type === "supervisor") && (
                <Button
                  basic
                  color="orange"
                  className="critical-followup-top-btn"
                  disabled={sendingFollowup}
                  onClick={openFollowupModal}
                >
                  <img src="/follow_up_request.svg" alt="" />
                  Email Site Follow-up
                </Button>
              )} */}
              <Button
                basic
                color="red"
                className="critical-summary-btn"
                onClick={() => set_mdl_summary_open(true)}
              >
                <Icon name="columns" />
                Critical Summary
              </Button>
              <Button
                color="red"
                onClick={() => set_mdl_critical_detail_open(true)}
              >
                <img src="/icon-add.svg" alt="icon" />
                Add Critical Unit
              </Button>
            </div>
          </div>

          <div className="critical-stats-row">
            <div className="critical-stat-card critical-stat-total">
              <div className="critical-stat-icon">
                <Icon name="clipboard list" />
              </div>
              <div className="critical-stat-body">
                <div className="critical-stat-count">
                  {statusCountsLoading ? "-" : statusCounts.total}
                </div>
                <div className="critical-stat-label">Total active Critical</div>
              </div>
            </div>
            <div
              className={`critical-stat-card critical-stat-ongoing${
                var_critcal_type === "ongoing" ? " is-active" : ""
              }`}
              onClick={() => onChangeCriticalType(null, { value: "ongoing" })}
            >
              <div className="critical-stat-icon">
                <Icon name="exclamation triangle" />
              </div>
              <div className="critical-stat-body">
                <div className="critical-stat-count">
                  {statusCountsLoading ? "-" : statusCounts.ongoing}
                </div>
                <div className="critical-stat-label">Ongoing</div>
              </div>
            </div>
            <div
              className={`critical-stat-card critical-stat-supervisor${
                var_critcal_type === "supervisor" ? " is-active" : ""
              }`}
              onClick={() =>
                onChangeCriticalType(null, { value: "supervisor" })
              }
            >
              <div className="critical-stat-icon">
                <Icon name="user shield" />
              </div>
              <div className="critical-stat-body">
                <div className="critical-stat-count">
                  {statusCountsLoading ? "-" : statusCounts.supervisor}
                </div>
                <div className="critical-stat-label">S.W.A.T</div>
              </div>
            </div>
            <div
              className={`critical-stat-card critical-stat-resolved${
                var_critcal_type === "resolved" ? " is-active" : ""
              }`}
              onClick={() => onChangeCriticalType(null, { value: "resolved" })}
            >
              <div className="critical-stat-icon">
                <Icon name="check circle" />
              </div>
              <div className="critical-stat-body">
                <div className="critical-stat-count">
                  {statusCountsLoading ? "-" : statusCounts.ytdResolved}
                </div>
                <div className="critical-stat-label">YTD Resolved</div>
              </div>
            </div>
          </div>

          <div className="app-card">
            <div className="app-card-header critical-header-card">
              <div className="app-card-title">
                Critical List (
                {searchResult && searchResult?.length > 0
                  ? searchResult?.length
                  : CriticalData?.length}
                )
              </div>
              <div className="app-content-search critical-search">
                <Search
                  placeholder="Search building name or client"
                  onSearchChange={doSearch}
                  showNoResults={false}
                  value={searchQuery}
                />
              </div>
              <div className="critical-filter-container">
                {schedulers?.length > 0 && (
                  <Select
                    placeholder="Select scheduler"
                    id="scheduler_type"
                    value={var_scheduler_list.value}
                    onChange={onChangeScheduler}
                    options={schedulers?.map((option) => ({
                      key: option.value,
                      value: option.value,
                      text: getSchedulerOptionLabel(option),
                    }))}
                  />
                )}
                <Select
                  name="critical_type"
                  id="critical_type"
                  placeholder="Select type"
                  options={critical_types}
                  selectOnBlur={false}
                  value={var_critcal_type}
                  onChange={onChangeCriticalType}
                />
                {var_critcal_type === "resolved" && (
                  <div className="critical-year-picker">
                    <YearPicker
                      id="download_year"
                      name="download_year"
                      value={downloadYear}
                      onChange={onChangeDownloadYear}
                      availableYears={availableDownloadYears}
                      placeholder="Year"
                    />
                  </div>
                )}
                <Button
                  color="light"
                  className={`critical-icon-btn${
                    var_critcal_type === "resolved"
                      ? !var_scheduler_list.value ||
                        !downloadYear ||
                        availableDownloadYears.length === 0
                        ? " is-locked"
                        : ""
                      : !(CriticalData?.length > 0)
                        ? " is-locked"
                        : ""
                  }`}
                  onClick={() => {
                    const isLocked =
                      var_critcal_type === "resolved"
                        ? !var_scheduler_list.value ||
                          !downloadYear ||
                          availableDownloadYears.length === 0
                        : !(CriticalData?.length > 0);
                    if (!isLocked) {
                      set_downlaod(true);
                    }
                  }}
                >
                  <img src="./download-critical.png" alt="download" />
                </Button>
                <Button
                  color="light"
                  className="critical-icon-btn"
                  onClick={() => set_refresh(true)}
                >
                  <img src="./refresh.svg" alt="refresh" />
                </Button>
              </div>
            </div>

            <div className="app-table-wrapper">
              <Table sortable unstackable verticalAlign="top">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell
                      width={2}
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      sorted={
                        current_sort?.sort_by === "building_address"
                          ? current_sort.sort_by_value === "ASC"
                            ? "ascending"
                            : "descending"
                          : null
                      }
                      onClick={() =>
                        set_current_sorted({
                          sort_by: "building_address",
                          sort_by_value:
                            current_sort?.sort_by_value === "ASC"
                              ? "DESC"
                              : "ASC",
                        })
                      }
                    >
                      <Popup
                        trigger={<span>Building Address</span>}
                        content="Building Address"
                        size="tiny"
                        position="top center"
                      />
                      {current_sort?.sort_by !== "building_address" && (
                        <Icon size="20" name="sort" color="black" />
                      )}
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      width={1}
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      sorted={
                        current_sort?.sort_by === "customer_name"
                          ? current_sort.sort_by_value === "ASC"
                            ? "ascending"
                            : "descending"
                          : null
                      }
                      onClick={() =>
                        set_current_sorted({
                          sort_by: "customer_name",
                          sort_by_value:
                            current_sort?.sort_by_value === "ASC"
                              ? "DESC"
                              : "ASC",
                        })
                      }
                    >
                      Client
                      {current_sort?.sort_by !== "customer_name" && (
                        <Icon size="20" name="sort" color="black" />
                      )}
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      width={1}
                      style={{ whiteSpace: "nowrap" }}
                      sorted={
                        current_sort?.sort_by === "unit_number"
                          ? current_sort.sort_by_value === "ASC"
                            ? "ascending"
                            : "descending"
                          : null
                      }
                      onClick={() =>
                        set_current_sorted({
                          sort_by: "unit_number",
                          sort_by_value:
                            current_sort?.sort_by_value === "ASC"
                              ? "DESC"
                              : "ASC",
                        })
                      }
                    >
                      Unit #
                      {current_sort?.sort_by !== "unit_number" && (
                        <Icon size="20" name="sort" color="black" />
                      )}
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      width={2}
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      sorted={
                        current_sort?.sort_by === "date_reported"
                          ? current_sort.sort_by_value === "ASC"
                            ? "ascending"
                            : "descending"
                          : null
                      }
                      onClick={() =>
                        set_current_sorted({
                          sort_by: "date_reported",
                          sort_by_value:
                            current_sort?.sort_by_value === "ASC"
                              ? "DESC"
                              : "ASC",
                        })
                      }
                    >
                      Date Reported
                      {current_sort?.sort_by !== "date_reported" && (
                        <Icon size="20" name="sort" color="black" />
                      )}
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                    >
                      Pest
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      width={1}
                    >
                      Recent Activity
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      width={1}
                      textAlign="center"
                      sorted={
                        current_sort?.sort_by ===
                        (var_critcal_type === "resolved"
                          ? "resolved_date"
                          : "last_service_date")
                          ? current_sort.sort_by_value === "ASC"
                            ? "ascending"
                            : "descending"
                          : null
                      }
                      onClick={() =>
                        set_current_sorted({
                          sort_by:
                            var_critcal_type === "resolved"
                              ? "resolved_date"
                              : "last_service_date",
                          sort_by_value:
                            current_sort?.sort_by_value === "ASC"
                              ? "DESC"
                              : "ASC",
                        })
                      }
                    >
                      <span>
                        {var_critcal_type === "resolved" ? "Resolved Date" : "LSD"}
                      </span>
                      {current_sort?.sort_by !==
                        (var_critcal_type === "resolved"
                          ? "resolved_date"
                          : "last_service_date") && (
                        <Icon size="20" name="sort" color="black" />
                      )}
                      {var_critcal_type !== "resolved" && (
                        <Popup
                          trigger={
                            <Icon
                              name="question circle outline"
                              size="small"
                              color="grey"
                              style={{ marginLeft: "4px", cursor: "pointer" }}
                            />
                          }
                          content="Last Scheduled Date"
                          size="tiny"
                          position="top center"
                        />
                      )}
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      width={1}
                      textAlign="center"
                    >
                      <span>TC</span>
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            size="small"
                            color="grey"
                            style={{ marginLeft: "4px", cursor: "pointer" }}
                          />
                        }
                        content="Treatment Count"
                        size="tiny"
                        position="top center"
                      />
                    </Table.HeaderCell>{" "}
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      width={1}
                      textAlign="center"
                    >
                      <span>CC</span>
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            size="small"
                            color="grey"
                            style={{ marginLeft: "4px", cursor: "pointer" }}
                          />
                        }
                        content="Compliance Count"
                        size="tiny"
                        position="top center"
                      />
                    </Table.HeaderCell>{" "}
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      width={1}
                      textAlign="center"
                    >
                      <span>CEC</span>
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            size="small"
                            color="grey"
                            style={{ marginLeft: "4px", cursor: "pointer" }}
                          />
                        }
                        content="Critical Email Count"
                        size="tiny"
                        position="top center"
                      />
                    </Table.HeaderCell>{" "}
                    {/* Increased width */}
                    <Table.HeaderCell
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      width={2}
                    >
                      Issue Tags
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        padding: "0px 100px  10px 10px",
                      }}
                    >
                      {var_critcal_type === "deleted"
                        ? "Delete Reason"
                        : "Note"}
                    </Table.HeaderCell>{" "}
                    {/* {var_critcal_type !== "deleted" && ( */}
                    <Table.HeaderCell
                      textAlign="center"
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                    >
                      Action
                    </Table.HeaderCell>
                    {/* )} */}
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {CriticalData &&
                    CriticalData?.map((data, i) => (
                      <Table.Row key={i}>
                        <Table.Cell width={2}>
                          <span
                            className="building-link"
                            onClick={() => {
                              set_default_pane(0);
                              history.push(
                                `/building-impersonate/${data?.buildingID}`,
                                { from: "/critical-section" },
                              );
                            }}
                          >
                            {data?.building_address +
                              " (" +
                              data?.location_number +
                              ")"}
                          </span>
                        </Table.Cell>
                        <Table.Cell width={1}>{data?.customer_name}</Table.Cell>
                        <Table.Cell
                          width={1}
                          onClick={() =>
                            handleModalUnitHistory(
                              data?.buildingID,
                              data?.unit_number,
                            )
                          }
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {data?.unit_number}
                        </Table.Cell>
                        <Table.Cell width={2}>
                          {moment(data?.date_reported).format("YYYY-MM-DD")}
                        </Table.Cell>
                        <Table.Cell width={1}>{data?.pest_name}</Table.Cell>
                        <Table.Cell width={1} style={{ whiteSpace: "nowrap" }}>
                          {data?.activity_level}
                        </Table.Cell>
                        <Table.Cell
                          width={1}
                          textAlign="center"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <span className="lsd-cell-content">
                            {var_critcal_type === "resolved"
                              ? data?.resolved_date
                                ? moment(data.resolved_date).format(
                                    "YYYY-MM-DD",
                                  )
                                : "N/A"
                              : moment(data?.last_service_date).format(
                                  "YYYY-MM-DD",
                                )}
                            {var_critcal_type !== "resolved" &&
                              (() => {
                                const signal = getLsdSignal(
                                  data?.last_service_date,
                                  data?.has_scheduled_order,
                                );
                                if (!signal) {
                                  return (
                                    <span className="lsd-signal-badge lsd-signal-badge-empty" />
                                  );
                                }
                                return (
                                  <Popup
                                    trigger={
                                      <span
                                        className="lsd-signal-badge"
                                        style={{ backgroundColor: signal.color }}
                                      />
                                    }
                                    content={signal.label}
                                    size="tiny"
                                    position="top center"
                                  />
                                );
                              })()}
                          </span>
                        </Table.Cell>
                        <Table.Cell width={1} textAlign="center">
                          {data?.treatment_count}
                        </Table.Cell>
                        <Table.Cell width={1} textAlign="center">
                          {data?.compliance_issue_count}
                        </Table.Cell>
                        <Table.Cell width={1} textAlign="center">
                          {data?.critical_email_count}
                        </Table.Cell>
                        <Table.Cell width={2}>
                          {data?.issue_tags?.length ? (
                            <>
                              {data.issue_tags.slice(0, 2).map((tag) => (
                                <Label
                                  key={tag}
                                  size="small"
                                  style={{
                                    margin: "2px",
                                    backgroundColor:
                                      issueTagColorMap[tag] || "#e0e0e0",
                                    color: "#333",
                                  }}
                                >
                                  {tag}
                                </Label>
                              ))}
                              {data.issue_tags.length > 2 && (
                                <Popup
                                  trigger={
                                    <Label
                                      size="small"
                                      style={{
                                        margin: "2px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      +{data.issue_tags.length - 2}
                                    </Label>
                                  }
                                  content={data.issue_tags.map((tag) => (
                                    <Label
                                      key={tag}
                                      size="small"
                                      style={{
                                        margin: "2px",
                                        backgroundColor:
                                          issueTagColorMap[tag] || "#e0e0e0",
                                        color: "#333",
                                      }}
                                    >
                                      {tag}
                                    </Label>
                                  ))}
                                  on="click"
                                  position="top center"
                                />
                              )}
                            </>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell
                          width={4}
                          style={{
                            textDecoration:
                              (var_critcal_type === "deleted"
                                ? data?.delete_reason?.length
                                : data?.note?.length) > 70
                                ? "underline"
                                : "none",
                            cursor:
                              (var_critcal_type === "deleted"
                                ? data?.delete_reason?.length
                                : data?.note?.length) > 70
                                ? "pointer"
                                : "default",
                          }}
                          onClick={
                            (var_critcal_type === "deleted"
                              ? data?.delete_reason?.length
                              : data?.note?.length) > 70
                              ? () =>
                                  HandleMdlDetails(
                                    var_critcal_type === "deleted"
                                      ? data?.delete_reason
                                      : data?.note,
                                    var_critcal_type === "deleted"
                                      ? "Delete Reason"
                                      : "Note",
                                  )
                              : null
                          }
                        >
                          {var_critcal_type === "deleted"
                            ? truncateText(data?.delete_reason, 70)
                            : truncateText(data?.note, 70)}
                        </Table.Cell>
                        {/* <Table.Cell style={{textDecoration: "underline",cursor: "pointer"}} onClick={()=>HandleMdlDetails(data.last_tech_comment,'Last Tech Comment')}>{truncateText(data.last_tech_comment,30)}</Table.Cell> */}
                        {/* <Table.Cell>{data.status}</Table.Cell> */}
                        {var_critcal_type !== "deleted" && (
                          <Table.Cell
                            className="td-action"
                            collapsing
                            textAlign="right"
                            style={{ paddingBottom: "8px" }}
                          >
                            {(data?.status === "ongoing" ||
                              data?.status === "supervisor") && (
                              <span className="send-followup-wrapper">
                                <Popup
                                  content={
                                    data?.isFullBuildingTreatment
                                      ? "Follow-up not applicable — this unit is on a Full Building treatment"
                                      : unitFollowupDisabled[data?.uuid]
                                      ? "Sending follow-up email…"
                                      : "Send Follow-up"
                                  }
                                  position="top center"
                                  trigger={
                                    <Label
                                      color="orange"
                                      style={{
                                        marginRight: "6px",
                                        opacity:
                                          unitFollowupDisabled[data?.uuid] ||
                                          data?.isFullBuildingTreatment
                                            ? 0.6
                                            : 1,
                                        cursor:
                                          unitFollowupDisabled[data?.uuid] ||
                                          data?.isFullBuildingTreatment
                                            ? "not-allowed"
                                            : "pointer",
                                      }}
                                      onClick={() => {
                                        if (
                                          data?.isFullBuildingTreatment ||
                                          unitFollowupDisabled[data?.uuid]
                                        ) {
                                          return;
                                        }
                                        handleSendUnitFollowupEmail(data);
                                      }}
                                    >
                                      <Icon
                                        name={
                                          unitFollowupDisabled[data?.uuid]
                                            ? "spinner"
                                            : "mail"
                                        }
                                        loading={
                                          !!unitFollowupDisabled[data?.uuid]
                                        }
                                      />
                                      Send
                                    </Label>
                                  }
                                />
                                {data?.last_followup_email_sent_at && (
                                  <span className="last-followup-sent-text">
                                    {moment(
                                      data.last_followup_email_sent_at,
                                    ).format("MM/DD/YYYY")}
                                  </span>
                                )}
                              </span>
                            )}
                            <Label
                              color={
                                data?.status === "ongoing" ||
                                data?.status === "supervisor"
                                  ? "green"
                                  : "blue"
                              }
                              onClick={() => handleEditCriticalUnit(data)}
                            >
                              {data?.status === "ongoing" ||
                              data?.status === "supervisor"
                                ? "Edit"
                                : "View"}
                            </Label>
                          </Table.Cell>
                        )}
                        {var_critcal_type === "deleted" && (
                          <Table.Cell
                            className="td-action"
                            collapsing
                            textAlign="right"
                          >
                            <Label
                              color={"blue"}
                              onClick={() => {
                                setIsRestoreModalOpen(true);
                                setRecordToRestore(data);
                              }}
                            >
                              {"Undo"}
                            </Label>
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                </Table.Body>
              </Table>
              {CriticalData?.length === 0 && (
                <div
                  className="nodata-placeholder"
                  style={{ paddingBottom: "10px" }}
                >
                  There is no Critical list to display
                </div>
              )}
            </div>
          </div>
          <AppPagination
            limit={limit}
            setLimit={setLimit}
            page={page}
            setPage={setPage}
            rowsTotal={CriticalData?.length}
          />
        </div>
      </div>
      {/***** MODAL: PROGRESS **************************************************************************/}
      <Modal
        id="mdl-progress"
        dimmer={"inverted"}
        open={var_mdl_progress}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        onClose={() => {}}
      >
        <MDL_PROGRESS
          set_mdl_open={set_mdl_progress}
          var_modaltitle="Please wait"
          var_message={"Stay on this page until this process finish."}
        ></MDL_PROGRESS>
      </Modal>

      {/***** END MODAL: PROGRESS **************************************************************************/}
      {/***** MODAL: ADD Critical Detail **************************************************************************/}
      <Modal
        id="mdl-critical-detail"
        style={{ overflow: "visible" }}
        dimmer={"inverted"}
        open={var_mdl_critical_detail_open}
        onClose={() => {}}
      >
        <MDL_ADD_CRITICAL_UNIT
          set_mdl_open={set_mdl_critical_detail_open}
          setPage={setPage}
          onListRefresh={refreshCriticalList}
        ></MDL_ADD_CRITICAL_UNIT>
      </Modal>
      {/***** END MODAL: ADD Critical Detail **************************************************************************/}
      {/***** MODAL: CRITICAL SUMMARY **************************************************************************/}
      <Modal
        id="mdl-critical-summary"
        dimmer={"inverted"}
        size="small"
        open={var_mdl_summary_open}
        onClose={() => set_mdl_summary_open(false)}
      >
        <MDL_CRITICAL_SUMMARY
          set_mdl_open={set_mdl_summary_open}
          schedulerID={var_scheduler_list.value}
        />
      </Modal>
      {/***** END MODAL: CRITICAL SUMMARY **************************************************************************/}
      {/***** MODAL: UPDATE Critical Detail **************************************************************************/}
      <Modal
        id="mdl-critical-detail"
        style={{ overflow: "visible" }}
        dimmer={"inverted"}
        open={var_mdl_update_critical_detail_open}
        onClose={() => {}}
      >
        <MDL_UPDATE_CRITICAL_UNIT
          set_mdl_open={set_mdl_update_critical_detail_open}
          SingleCriticalData={SingleCriticalData}
          setPage={setPage}
          onListRefresh={refreshCriticalList}
        ></MDL_UPDATE_CRITICAL_UNIT>
      </Modal>
      {/***** END MODAL: UPDATE Critical Detail **************************************************************************/}
      {/***** MODAL: UNITS HISTORY **************************************************************************/}
      <Modal
        id="mdl-unit-history"
        dimmer={"inverted"}
        size="large"
        open={var_mdl_unit_history}
        onClose={() => {
          set_mdl_unit_history(false);
          set_selected_unit(false);
        }}
      >
        <MDL_UNIT_HISTORY
          unit_number={selected_unit}
          set_mdl_open={set_mdl_unit_history}
          building_id={building_id}
          set_selected_unit={set_selected_unit}
        />
      </Modal>

      {/***** MODAL: UNITS HISTORY **************************************************************************/}
      {/***** MODAL: UNITS HISTORY **************************************************************************/}
      <Modal
        id="mdl-view-detail"
        dimmer={"inverted"}
        size="large"
        open={var_mdl_view_detail_open}
        onClose={() => {
          set_mdl_view_detail_open(false);
        }}
      >
        <MDL_VIEW_DETAILS
          set_mdl_open={set_mdl_view_detail_open}
          data={var_detail_data}
          title={var_detail_title}
        />
      </Modal>
      {/***** MODAL: UNITS HISTORY **************************************************************************/}
      {/***** MODAL: UNDO DELETE ACTION **************************************************************************/}
      {isRestoreModalOpen && (
        <RestoreModal
          open={isRestoreModalOpen}
          onClose={() => {
            setIsRestoreModalOpen(false);
            setRecordToRestore(null);
          }}
          handleSubmit={UpdateCriticalData}
          unitDetails={recordToRestore}
          tabOptions={[
            {
              text: "Resolved",
              value: "resolved",
            },
            {
              text: "Ongoing",
              value: "ongoing",
            },
            {
              text: "S.W.A.T",
              value: "supervisor",
            },
          ]}
        />
      )}
      <MDL_SEND_FOLLOWUP
        open={isFollowupModalOpen}
        onClose={() => {
          if (!sendingFollowup) {
            setFollowupError("");
            setIsFollowupModalOpen(false);
          }
        }}
        onConfirm={handleConfirmSendFollowupEmail}
        schedulers={schedulers}
        defaultSchedulerIds={getDefaultFollowupSchedulerIds()}
        criticalType={var_critcal_type}
        loading={sendingFollowup}
        errorMessage={followupError}
      />
      {/***** MODAL: UNDO DELETE ACTION **************************************************************************/}
    </>
  );
}

export default CriticalList;
